import { useState, useEffect, useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import { getRulesetFeatures } from '@character-creator/data';
import {
  useCharacterCreationStore,
  DOCK_TABS,
  type DockTab,
  type EquipmentSubTab,
} from '../../stores/characterCreationStore';
import { useDockPosition, useIsMobile } from '../../hooks/useMediaQuery';
import { useAutoSaveToCatalog } from '../../hooks/useAutoSaveToCatalog';
import { useCharacterReadiness } from '../../hooks/useCharacterReadiness';
import { DockNavigation, type ValidationStatus } from './DockNavigation';
import { ReadinessProgressBar } from './ReadinessProgressBar';
import { BenefitsPreviewPanel } from './BenefitsPreviewPanel';
import { PreviewSheet } from './PreviewSheet';
import { SaveStatusIndicator } from '../creation-wizard/SaveStatusIndicator';
import { ExpandIcon, CollapseIcon, CheckIcon, ChevronUpIcon } from '../ui/Icons';
import {
  BasicsStep,
  RaceStep,
  ClassStep,
  AbilityScoresStep,
  BackgroundStep,
  EquipmentStep,
  CombatStep,
  SpellsStep,
} from '../creation-wizard/steps';

export function CharacterBuilder() {
  const {
    currentDockTab,
    setDockTab,
    getDockTabStatus,
    identitySubTab,
    equipmentSubTab,
  } = useCharacterCreationStore();
  const dockPosition = useDockPosition();
  const isMobile = useIsMobile();
  const [showPreviewSheet, setShowPreviewSheet] = useState(false);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const mainContentRef = useRef<HTMLElement>(null);

  // Auto-save to catalog
  const { status: saveStatus, saveNow } = useAutoSaveToCatalog();

  // Character readiness
  const readiness = useCharacterReadiness();

  // Build validation status for all tabs
  const validationStatus: Record<DockTab, ValidationStatus> = {
    identity: mapStatus(getDockTabStatus('identity')),
    equipment: mapStatus(getDockTabStatus('equipment')),
    spells: mapStatus(getDockTabStatus('spells')),
  };

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const currentIndex = DOCK_TABS.indexOf(currentDockTab);

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDockTab(DOCK_TABS[currentIndex - 1]);
        }
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentIndex < DOCK_TABS.length - 1) {
          setDockTab(DOCK_TABS[currentIndex + 1]);
        }
      }
    },
    [currentDockTab, setDockTab]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll to top when dock tab changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentDockTab]);

  // Render content based on current dock tab
  const renderTabContent = () => {
    switch (currentDockTab) {
      case 'identity':
        return (
          <div className="space-y-6">
            <IdentitySubTabs />
            {identitySubTab === 'basics' && <BasicsStep />}
            {identitySubTab === 'race' && <RaceStep />}
            {identitySubTab === 'class' && <ClassStep />}
            {identitySubTab === 'abilities' && <AbilityScoresStep />}
            {identitySubTab === 'background' && <BackgroundStep />}
          </div>
        );

      case 'equipment':
        return (
          <div className="space-y-6">
            <EquipmentSubTabs />
            {equipmentSubTab === 'inventory' && <EquipmentStep />}
            {equipmentSubTab === 'weapons' && <CombatStep />}
            {equipmentSubTab === 'currency' && <CurrencySection />}
          </div>
        );

      case 'spells':
        return <SpellsStep />;

      default:
        return null;
    }
  };

  return (
    <div
      className={clsx(
        'flex min-h-[calc(100vh-4rem)] bg-background-primary',
        dockPosition === 'left' ? 'pl-24' : 'pb-24'
      )}
    >
      {/* Dock Navigation */}
      <DockNavigation
        currentTab={currentDockTab}
        onTabChange={setDockTab}
        validationStatus={validationStatus}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 min-w-0">
        {/* Editor Panel */}
        <main ref={mainContentRef} className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-4 md:p-6">
            <ReadinessProgressBar readiness={readiness} />
            <div className="animate-fade-in">{renderTabContent()}</div>
          </div>
        </main>

        {/* Preview Panel - Desktop Only (full preview) */}
        {!isMobile && (
          <aside
            className={clsx(
              'border-l border-border-default bg-background-card overflow-y-auto transition-all duration-300',
              isPreviewExpanded ? 'w-[600px]' : 'w-96'
            )}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-background-card border-b border-border-default">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-text-primary">Character Preview</h2>
                <SaveStatusIndicator status={saveStatus} onRetry={saveNow} />
              </div>
              <button
                type="button"
                onClick={() => setIsPreviewExpanded(!isPreviewExpanded)}
                className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
                aria-label={isPreviewExpanded ? 'Collapse preview' : 'Expand preview'}
              >
                {isPreviewExpanded ? (
                  <CollapseIcon className="w-5 h-5" />
                ) : (
                  <ExpandIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            <BenefitsPreviewPanel isFullPanel showExportButtons />
          </aside>
        )}
      </div>

      {/* Mobile Mini Preview Bar */}
      {isMobile && (
        <MobilePreviewBar onOpenPreview={() => setShowPreviewSheet(true)} />
      )}

      {/* Mobile Preview Sheet */}
      <PreviewSheet isOpen={showPreviewSheet} onClose={() => setShowPreviewSheet(false)} />
    </div>
  );
}

// Map store status to dock status
function mapStatus(status: 'incomplete' | 'in-progress' | 'complete'): ValidationStatus {
  switch (status) {
    case 'complete':
      return 'valid';
    case 'in-progress':
      return 'incomplete';
    case 'incomplete':
    default:
      return 'incomplete';
  }
}

// Identity sub-tabs component — uses readiness hook for completeness
function IdentitySubTabs() {
  const { identitySubTab, setIdentitySubTab, draft } = useCharacterCreationStore();
  const terminology = getRulesetFeatures(draft.rulesetId).ui.terminology;
  const readiness = useCharacterReadiness();

  // Map readiness items to sub-tab completeness
  const readinessMap = new Map(
    readiness.items
      .filter((i) => i.subTab)
      .map((i) => [i.subTab!, i.complete])
  );

  const subTabs = [
    { id: 'basics' as const, label: 'Basics' },
    { id: 'race' as const, label: terminology.race },
    { id: 'class' as const, label: 'Class' },
    { id: 'abilities' as const, label: 'Abilities' },
    { id: 'background' as const, label: 'Background' },
  ];

  return (
    <div className="flex gap-2 overflow-x-auto pt-1 pb-2 scrollbar-hide">
      {subTabs.map((tab) => {
        const complete = readinessMap.get(tab.id) ?? false;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => setIdentitySubTab(tab.id)}
            className={clsx(
              'relative px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all',
              identitySubTab === tab.id
                ? 'bg-background-elevated text-accent-gold border border-accent-gold/40'
                : 'bg-background-elevated text-text-secondary hover:text-text-primary hover:bg-background-card'
            )}
          >
            {tab.label}
            {/* Status indicator */}
            {identitySubTab !== tab.id && complete && (
              <span className="absolute -top-1 -right-1 text-success">
                <CheckIcon className="w-3 h-3" />
              </span>
            )}
            {identitySubTab !== tab.id && !complete && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-danger" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// Equipment sub-tabs component — simpler than Identity (no completion dots, equipment is advisory)
function EquipmentSubTabs() {
  const { equipmentSubTab, setEquipmentSubTab } = useCharacterCreationStore();

  const subTabs: { id: EquipmentSubTab; label: string }[] = [
    { id: 'inventory', label: 'Inventory' },
    { id: 'weapons', label: 'Weapons & Attacks' },
    { id: 'currency', label: 'Currency' },
  ];

  return (
    <div className="flex gap-2 overflow-x-auto pt-1 pb-2 scrollbar-hide">
      {subTabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => setEquipmentSubTab(tab.id)}
          className={clsx(
            'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all',
            equipmentSubTab === tab.id
              ? 'bg-background-elevated text-accent-gold border border-accent-gold/40'
              : 'bg-background-elevated text-text-secondary hover:text-text-primary hover:bg-background-card'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// Currency section extracted from EquipmentStep for the Currency sub-tab
function CurrencySection() {
  const { draft, setCurrency } = useCharacterCreationStore();

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
          Currency
        </h2>
        <p className="text-text-secondary">
          Track your character's starting money. Your DM may grant starting gold instead of equipment.
        </p>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold text-text-primary mb-4">
          Starting Money
        </h3>
        <div className="grid grid-cols-5 gap-2">
          {(['cp', 'sp', 'ep', 'gp', 'pp'] as const).map((coin) => (
            <div key={coin} className="text-center">
              <input
                type="number"
                min="0"
                value={draft.currency?.[coin] ?? 0}
                onChange={(e) => setCurrency({ [coin]: Math.max(0, parseInt(e.target.value) || 0) })}
                className="w-full px-2 py-2 rounded-lg border border-border-default bg-background-secondary text-text-primary text-center focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:border-accent-gold"
              />
              <div className={clsx(
                'text-xs mt-1 font-medium uppercase',
                coin === 'gp' ? 'text-accent-gold' : 'text-text-muted'
              )}>
                {coin}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Mobile preview bar at bottom
function MobilePreviewBar({ onOpenPreview }: { onOpenPreview: () => void }) {
  const { draft } = useCharacterCreationStore();
  // Compute characterLevel from classLevels (source of truth) to avoid persist desync bug
  const characterLevel = draft.classLevels.length > 0
    ? draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
    : 1;

  return (
    <div className="fixed bottom-20 left-0 right-0 z-30 bg-background-card/95 backdrop-blur-sm border-t border-border-default px-4 py-3 safe-area-bottom">
      <button
        type="button"
        onClick={onOpenPreview}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-gold/20 flex items-center justify-center">
            <span className="text-accent-gold font-bold">
              {draft.name ? draft.name.charAt(0).toUpperCase() : '?'}
            </span>
          </div>
          <div className="text-left">
            <div className="font-medium text-text-primary">
              {draft.name || 'Unnamed Character'}
            </div>
            <div className="text-xs text-text-muted">
              Lv {characterLevel} • Tap to preview
            </div>
          </div>
        </div>
        <ChevronUpIcon className="w-5 h-5 text-text-muted" />
      </button>
    </div>
  );
}
